import React, { useMemo, useState } from 'react';
import { Button, Card, HTMLSelect, HTMLTable, Icon, SegmentedControl, Tag, Tooltip } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Link, useLoaderData, useLocation, useNavigate, useNavigation, useParams, useRevalidator } from 'react-router-dom';
import { ExecutionSummary, WhatChanged, WhatChangedComparison, WhatChangedRun, WhatChangedValue } from '../api/cppmodelApi';
import SimulationGraph, { getLegendItems } from './SimulationGraph';
import { ApiGraphData, getTestStatus, normalizeGraphData, TestStatus } from './SimulationResults';
import { useResizablePane } from './useResizablePane';
import { buildDifferenceData, getSignalDifferences, maxAbsDifference } from './runDifference';
import { msToSeconds } from '../utils/time';

type SignalPresence = 'both' | 'current' | 'previous';
// Both runs drawn over each other, or current − previous.
type GraphView = 'overlay' | 'difference';

interface ParameterChange {
    name: string;
    old: WhatChangedValue | null;
    new: WhatChangedValue | null;
}

interface RuleChange {
    name: string;
    previous: string | null;
    current: string | null;
}

const describeRun = (run: WhatChangedRun) => `#${run.run_id}${run.timestamp ? ` · ${run.timestamp} UTC` : ''}`;

const describeTestStatus = (status: TestStatus | null) => {
    if (!status) {
        return 'no step results';
    }

    return status.passed
        ? 'passed'
        : `failed (${status.failedSteps} of ${status.totalSteps} steps${status.firstFailureTime !== null ? `, first at t = ${msToSeconds(status.firstFailureTime)} s` : ''})`;
};

// Changed parameters first, then added, then removed, each by name.
const getParameterChanges = (whatChanged: WhatChanged): ParameterChange[] => {
    const byName = (a: ParameterChange, b: ParameterChange) => a.name.localeCompare(b.name);

    return [
        ...whatChanged.parameters.changed.map((change) => ({ name: change.name, old: change.old, new: change.new })).sort(byName),
        ...whatChanged.parameters.added.map((change) => ({ name: change.name, old: null, new: change.new })).sort(byName),
        ...whatChanged.parameters.removed.map((change) => ({ name: change.name, old: change.old, new: null })).sort(byName),
    ];
};

// Verification rules whose status differs between the runs, or that only one
// run has.
const getRuleChanges = (current: ApiGraphData, previous: ApiGraphData): RuleChange[] => {
    const names = Array.from(new Set([
        ...Object.keys(previous.verification ?? {}),
        ...Object.keys(current.verification ?? {}),
    ])).sort((a, b) => a.localeCompare(b));

    return names
        .map((name) => ({
            name,
            previous: previous.verification?.[name]?.status ?? null,
            current: current.verification?.[name]?.status ?? null,
        }))
        .filter((change) => change.previous !== change.current);
};

// Legend name -> which runs have the signal, from the server's comparison.
const getSignalPresence = (whatChanged: WhatChanged) => new Map<string, SignalPresence>([
    ...whatChanged.signals.inputs.map((signal) => [`In: ${signal.name}`, signal.in] as const),
    ...whatChanged.signals.outputs.map((signal) => [`Out: ${signal.name}`, signal.in] as const),
]);

const formatValue = (value: WhatChangedValue | null) => value === null ? '–' : `${value}`;

// How much a signal differs between the runs, for its legend row.
const describeDifference = (maxDifference: number | undefined, presence: SignalPresence | undefined) => {
    if (presence && presence !== 'both') {
        return 'not in the difference';
    }

    if (maxDifference === undefined) {
        return null;
    }

    return maxDifference === 0 ? 'same in both runs' : `max |Δ| ${Number(maxDifference.toPrecision(4))}`;
};

// Option label of an execution in the run pickers.
const describeExecution = (execution: ExecutionSummary) => (
    `#${execution.id} · ${execution.createdAt} UTC${execution.status === 'completed' ? '' : ` · ${execution.status}`}`
);

// The executions to pick from, including the compared ones when they are
// older than the fetched history.
const getRunOptions = (executions: ExecutionSummary[], whatChanged: WhatChanged) => {
    const options = executions.map((execution) => ({ value: execution.id, label: describeExecution(execution) }));

    [whatChanged.current, whatChanged.previous].forEach((run) => {
        if (!options.some((option) => option.value === run.run_id)) {
            options.push({ value: run.run_id, label: describeRun(run) });
        }
    });

    return options;
};

const PRESENCE_TAGS: Record<Exclude<SignalPresence, 'both'>, { label: string; title: string }> = {
    current: { label: 'new', title: 'Only in the current run' },
    previous: { label: 'gone', title: 'Only in the previous run' },
};

const WhatChangedPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const navigation = useNavigation();
    const revalidator = useRevalidator();
    const { simulationId } = useParams();
    // Only in-app navigation passes the name; a shared link has just the id.
    const simulationName = location.state?.simulationName ?? simulationId;
    const { whatChanged, current: rawCurrent, previous: rawPrevious, executions, error } = useLoaderData() as WhatChangedComparison;

    const { paneWidth, isResizingPane, bodyRef, splitterProps } = useResizablePane();
    const [traceVisibility, setTraceVisibility] = useState<Record<string, boolean>>({});
    const [graphView, setGraphView] = useState<GraphView>('overlay');

    const current = useMemo(() => normalizeGraphData(rawCurrent), [rawCurrent]);
    const previous = useMemo(() => normalizeGraphData(rawPrevious), [rawPrevious]);
    const legendItems = useMemo(() => getLegendItems(current, previous), [current, previous]);
    const differences = useMemo(() => getSignalDifferences(current, previous), [current, previous]);
    const differenceData = useMemo(() => buildDifferenceData(current, differences), [current, differences]);
    const maxDifferences = useMemo(() => new Map(
        Array.from(differences).map(([name, difference]) => [name, maxAbsDifference(difference)] as const)
    ), [differences]);
    const signalPresence = useMemo(() => whatChanged ? getSignalPresence(whatChanged) : new Map<string, SignalPresence>(), [whatChanged]);
    const parameterChanges = useMemo(() => whatChanged ? getParameterChanges(whatChanged) : [], [whatChanged]);
    const ruleChanges = useMemo(() => getRuleChanges(current, previous), [current, previous]);
    const runOptions = useMemo(() => whatChanged ? getRunOptions(executions, whatChanged) : [], [executions, whatChanged]);
    const currentTestStatus = useMemo(() => getTestStatus(current.results), [current.results]);
    const previousTestStatus = useMemo(() => getTestStatus(previous.results), [previous.results]);

    const isLoading = navigation.state === 'loading' || revalidator.state === 'loading';
    const isPinned = new URLSearchParams(location.search).has('current');
    const addedSignals = Array.from(signalPresence.values()).filter((presence) => presence === 'current').length;
    const removedSignals = Array.from(signalPresence.values()).filter((presence) => presence === 'previous').length;

    const showRuns = (currentId: string | null, previousId?: string) => {
        const query = new URLSearchParams();

        if (currentId) {
            query.set('current', currentId);

            if (previousId) {
                query.set('previous', previousId);
            }
        }

        const search = query.toString();
        navigate(`/what-changed/${simulationId}${search ? `?${search}` : ''}`, { state: { simulationName } });
    };

    const toggleTrace = (name: string) => {
        setTraceVisibility((prevVisibility) => ({
            ...prevVisibility,
            [name]: prevVisibility[name] === false,
        }));
    };

    // Show only the given signal, or show every signal again when it already
    // is the only one visible.
    const isolateTrace = (name: string) => {
        setTraceVisibility((prevVisibility) => {
            const isAlreadyIsolated = legendItems.every((item) => (
                (prevVisibility[item.name] !== false) === (item.name === name)
            ));

            if (isAlreadyIsolated) {
                return {};
            }

            return Object.fromEntries(legendItems.map((item) => [item.name, item.name === name]));
        });
    };

    const getRunPath = (runId: string) => `/results/${simulationId}/executions/${runId}`;
    const openResults = () => navigate(whatChanged ? getRunPath(whatChanged.current.run_id) : `/results/${simulationId}`, { state: { simulationName } });

    const runLink = (run: WhatChangedRun) => (
        <Link to={getRunPath(run.run_id)} state={{ simulationName }} title={`Open the results of run #${run.run_id}`}>
            {describeRun(run)}
        </Link>
    );

    if (!whatChanged) {
        return (
            <div className="cm-page">
                <div className="cm-page-header">
                    <div>
                        <h1 className="cm-page-title">What changed in {simulationName}</h1>
                        <p className="cm-page-subtitle">Compare two runs of the simulation: its code, parameters, signals and results.</p>
                    </div>
                    <div className="cm-page-header-actions">
                        <Button icon={IconNames.TimelineLineChart} onClick={openResults}>Open results</Button>
                    </div>
                </div>
                <div className="cm-empty">
                    {executions.length < 2
                        ? 'This simulation needs at least two runs to compare.'
                        : error}
                    {isPinned && (
                        <div style={{ marginTop: '12px' }}>
                            <Button onClick={() => showRuns(null)}>Compare the latest runs</Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="cm-results">
            <div className="cm-page-header" style={{ marginBottom: '16px' }}>
                <div>
                    <h1 className="cm-page-title">What changed in {simulationName}</h1>
                    <p className="cm-page-subtitle">
                        Run {runLink(whatChanged.current)} compared with run {runLink(whatChanged.previous)}
                    </p>
                </div>
                <div className="cm-page-header-actions">
                    <Button icon={IconNames.TimelineLineChart} onClick={openResults}>Open current run</Button>
                </div>
            </div>

            <div className={`cm-results-body${isResizingPane ? ' is-resizing' : ''}${isLoading ? ' is-loading' : ''}`} ref={bodyRef}>
                <Card className="cm-graph-card">
                    <div className="cm-graph-toolbar">
                        <SegmentedControl
                            small
                            options={[{ label: 'Overlay', value: 'overlay' }, { label: 'Difference', value: 'difference' }]}
                            value={graphView}
                            onValueChange={(value) => setGraphView(value as GraphView)}
                        />
                        {graphView === 'overlay' ? (
                            <span className="cm-diff-key">
                                <span className="cm-diff-key-line" /> Current #{whatChanged.current.run_id}
                                <span className="cm-diff-key-line is-previous" /> Previous #{whatChanged.previous.run_id}
                            </span>
                        ) : (
                            <span className="cm-diff-key">
                                Current #{whatChanged.current.run_id} − previous #{whatChanged.previous.run_id}, over the time both runs cover
                            </span>
                        )}
                        <span className="cm-graph-hint">
                            {graphView === 'overlay'
                                ? 'Click the graph to place markers A and B and read both runs at them · Scroll to zoom'
                                : 'Zero means no difference · Click the graph to place markers A and B · Scroll to zoom'}
                        </span>
                    </div>
                    <div className="cm-graph-area">
                        {graphView === 'overlay' ? (
                            <SimulationGraph
                                data={current}
                                previousData={previous}
                                traceVisibility={traceVisibility}
                            />
                        ) : (
                            <SimulationGraph
                                data={differenceData}
                                traceVisibility={traceVisibility}
                            />
                        )}
                    </div>
                </Card>

                <div className="cm-results-splitter" aria-label="Resize changes pane" {...splitterProps} />

                <Card className="cm-params-card" style={{ '--cm-params-width': `${paneWidth}px` } as React.CSSProperties}>
                    <h2 className="cm-section-title">Summary</h2>
                    <ul className="cm-diff-summary">
                        <li>
                            <Icon icon={IconNames.Code} />
                            {whatChanged.code_changed === null
                                ? 'Unknown whether the code changed: a run has no binary fingerprint'
                                : whatChanged.code_changed ? 'The code was rebuilt' : 'Same build of the code'}
                        </li>
                        <li>
                            <Icon icon={IconNames.Properties} />
                            {parameterChanges.length === 0
                                ? 'No parameter changed'
                                : `${parameterChanges.length} parameter${parameterChanges.length === 1 ? '' : 's'} changed`}
                        </li>
                        <li>
                            <Icon icon={IconNames.TimelineLineChart} />
                            {addedSignals + removedSignals === 0
                                ? 'Same signals'
                                : [addedSignals > 0 && `${addedSignals} new signal${addedSignals === 1 ? '' : 's'}`,
                                    removedSignals > 0 && `${removedSignals} signal${removedSignals === 1 ? '' : 's'} gone`].filter(Boolean).join(', ')}
                        </li>
                        {(currentTestStatus || previousTestStatus) && (
                            <li className={currentTestStatus && !currentTestStatus.passed ? 'is-failed' : ''}>
                                <Icon icon={currentTestStatus?.passed ? IconNames.TickCircle : IconNames.Error} />
                                <span>
                                    Test {previousTestStatus?.passed === currentTestStatus?.passed ? 'still ' : ''}{describeTestStatus(currentTestStatus)}
                                    {previousTestStatus?.passed !== currentTestStatus?.passed && (
                                        <span className="cm-diff-was"> (was {describeTestStatus(previousTestStatus)})</span>
                                    )}
                                </span>
                            </li>
                        )}
                    </ul>

                    <h2 className="cm-section-title">Parameters</h2>
                    {parameterChanges.length === 0 ? (
                        <p className="cm-diff-none">Both runs read the same parameter values.</p>
                    ) : (
                        <HTMLTable striped compact className="cm-diff-table">
                            <thead>
                                <tr>
                                    <th>Parameter</th>
                                    <th className="is-number">Previous</th>
                                    <th className="is-number">Current</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parameterChanges.map((change) => (
                                    <tr key={change.name}>
                                        <td>
                                            {change.name}
                                            {change.old === null && <Tag minimal intent="success" className="cm-diff-tag">new</Tag>}
                                            {change.new === null && <Tag minimal intent="danger" className="cm-diff-tag">gone</Tag>}
                                        </td>
                                        <td className="is-number">{formatValue(change.old)}</td>
                                        <td className="is-number"><strong>{formatValue(change.new)}</strong></td>
                                    </tr>
                                ))}
                            </tbody>
                        </HTMLTable>
                    )}

                    {ruleChanges.length > 0 && (
                        <>
                            <h2 className="cm-section-title">Verification</h2>
                            <HTMLTable striped compact className="cm-diff-table">
                                <thead>
                                    <tr>
                                        <th>Rule</th>
                                        <th className="is-number">Previous</th>
                                        <th className="is-number">Current</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ruleChanges.map((change) => (
                                        <tr key={change.name}>
                                            <td>{change.name}</td>
                                            <td className="is-number">{change.previous ?? '–'}</td>
                                            <td className={`is-number${change.current === 'pass' ? '' : ' is-failed'}`}>
                                                <strong>{change.current ?? '–'}</strong>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </HTMLTable>
                        </>
                    )}

                    {/* Graph legend: click a signal to show or hide both runs of
                        it, double-click to show only that signal */}
                    {legendItems.length > 0 && (
                        <div className="cm-legend">
                            <h2 className="cm-section-title">Signals</h2>
                            <ul className="cm-legend-list">
                                {legendItems.map((item) => {
                                    const isVisible = traceVisibility[item.name] !== false;
                                    const presence = signalPresence.get(item.name);
                                    const tag = presence && presence !== 'both' ? PRESENCE_TAGS[presence] : null;
                                    const difference = describeDifference(maxDifferences.get(item.name), presence);

                                    return (
                                        <li key={item.name} className="cm-legend-row">
                                            <button
                                                type="button"
                                                className={`cm-legend-item${isVisible ? '' : ' is-hidden'}`}
                                                onClick={() => toggleTrace(item.name)}
                                                onDoubleClick={() => isolateTrace(item.name)}
                                                aria-pressed={isVisible}
                                                title={`${isVisible ? 'Hide from graph' : 'Show in graph'} (double-click to isolate)`}
                                            >
                                                <span className={`cm-legend-swatch is-${item.dash}`} style={{ color: item.color }} />
                                                <span className="cm-legend-text">
                                                    <span className="cm-legend-name">{item.name}</span>
                                                    {difference && <span className="cm-legend-transform">{difference}</span>}
                                                </span>
                                                {tag && (
                                                    <Tag minimal intent={presence === 'current' ? 'success' : 'danger'} className="cm-diff-tag" title={tag.title}>
                                                        {tag.label}
                                                    </Tag>
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </Card>
            </div>

            {/* Sticky footer: which runs are compared */}
            <div className="cm-results-footer">
                <label className="cm-footer-field">Previous
                    <HTMLSelect
                        value={whatChanged.previous.run_id}
                        options={runOptions.filter((option) => option.value !== whatChanged.current.run_id)}
                        onChange={(event) => showRuns(whatChanged.current.run_id, event.currentTarget.value)}
                        disabled={isLoading}
                    />
                </label>
                <Tooltip content="Swap the current and previous runs">
                    <Button
                        minimal
                        icon={IconNames.SwapHorizontal}
                        aria-label="Swap the current and previous runs"
                        onClick={() => showRuns(whatChanged.previous.run_id, whatChanged.current.run_id)}
                        disabled={isLoading}
                    />
                </Tooltip>
                <label className="cm-footer-field">Current
                    <HTMLSelect
                        value={whatChanged.current.run_id}
                        options={runOptions}
                        // The server compares it with the run before it.
                        onChange={(event) => showRuns(event.currentTarget.value)}
                        disabled={isLoading}
                    />
                </label>
                <div className="cm-footer-actions">
                    {isPinned && (
                        <Tooltip content="Compare the two latest runs">
                            <Button icon={IconNames.Updated} onClick={() => showRuns(null)} disabled={isLoading}>
                                Latest runs
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip content={isPinned ? 'Reload the compared runs' : 'Compare the two latest runs again'}>
                        <Button icon={IconNames.Refresh} loading={isLoading} onClick={() => revalidator.revalidate()}>
                            Refresh
                        </Button>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

// Moving to another simulation starts from its own view.
const WhatChangedView: React.FC = () => {
    const { simulationId } = useParams();

    return <WhatChangedPage key={simulationId} />;
};

export default WhatChangedView;
